import React, {useEffect, useState} from 'react';

/** One fee band: withdrawing between min and max (inclusive) costs fee. All MWK. */
export interface FeeBand {
    min: number;
    max: number;
    fee: number;
}

export interface OperatorFees {
    bands: FeeBand[];
    /** When the table was last updated here (ISO date). */
    updatedAt?: string;
    sourceUrl?: string;
}

/** paymentConfig/global.withdrawalFees — what each mobile money operator
 * charges a seller to cash out at an agent. Read by the apps' product price
 * helper; Shopped never charges or collects it. */
export type WithdrawalFees = Partial<Record<'airtel' | 'tnm', OperatorFees>> & {
    /** Government levy on cash-outs above `above`, both networks: `rate` of the amount (0.0005 = 0.05%). */
    levy?: {rate: number; above: number};
};

const OPERATORS: {key: 'airtel' | 'tnm'; label: string; source: string}[] = [
    {key: 'airtel', label: 'Airtel Money', source: 'https://www.airtel.mw/airtelmoney/transaction_fees'},
    {key: 'tnm', label: 'TNM Mpamba', source: 'https://www.tnm.co.mw/mpamba'},
];

/**
 * Parses a pasted fee table: one band per line, three numbers each
 * (from, to, fee), separated by tabs/spaces; thousands commas allowed. Lines
 * without three numbers (headers like "From To Fee") are skipped.
 */
export function parseFeeTable(text: string): FeeBand[] {
    const bands: FeeBand[] = [];
    for (const line of text.split(/\r?\n/)) {
        const nums = (line.match(/\d[\d,]*(?:\.\d+)?/g) || []).map((n) => Number(n.replace(/,/g, '')));
        if (nums.length >= 3) bands.push({min: nums[0], max: nums[1], fee: nums[2]});
    }
    return bands.sort((a, b) => a.min - b.min);
}

/** Problems worth fixing before saving: bad bands, gaps and overlaps. */
export function checkFeeTable(bands: FeeBand[]): string[] {
    const issues: string[] = [];
    if (bands.length === 0) issues.push('No bands found — paste rows of: from, to, fee.');
    bands.forEach((b, i) => {
        if (b.max < b.min) issues.push(`Row ${i + 1}: "to" (${b.max}) is below "from" (${b.min}).`);
        if (b.fee < 0) issues.push(`Row ${i + 1}: fee is negative.`);
        const prev = bands[i - 1];
        if (prev) {
            if (b.min <= prev.max) issues.push(`Rows ${i} and ${i + 1} overlap (${prev.max} vs ${b.min}).`);
            else if (b.min > prev.max + 1) issues.push(`Gap between ${prev.max} and ${b.min}.`);
        }
    });
    return issues;
}

const fmt = (n: number) => n.toLocaleString('en-US');

const OperatorTable = ({op, value, onSave}: {
    op: typeof OPERATORS[number];
    value?: OperatorFees;
    onSave: (fees: OperatorFees) => Promise<void>;
}) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState('');
    const [preview, setPreview] = useState<FeeBand[] | null>(null);

    useEffect(() => {
        setText((value?.bands ?? []).map((b) => `${b.min}\t${b.max}\t${b.fee}`).join('\n'));
    }, [value]);

    const issues = preview ? checkFeeTable(preview) : [];
    const bands = value?.bands ?? [];

    return (
        <div style={{borderTop: '1px solid #f5f5f5', padding: '12px 0'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                <div>
                    <div style={{fontSize: 13, fontWeight: 600, color: '#333'}}>{op.label}</div>
                    <div style={{fontSize: 11, color: '#aaa'}}>
                        {bands.length ? `${bands.length} bands` : 'Not set — the price helper shows nothing for these sellers'}
                        {value?.updatedAt ? ` · updated ${value.updatedAt.slice(0, 10)}` : ''}
                        {' · '}<a href={value?.sourceUrl || op.source} target="_blank" rel="noreferrer">source</a>
                    </div>
                </div>
                {!editing && (
                    <button style={btn} onClick={() => { setEditing(true); setPreview(null); }}>
                        {bands.length ? 'Update table' : 'Add table'}
                    </button>
                )}
            </div>

            {!editing && bands.length > 0 && (
                <table style={{width: '100%', fontSize: 12, borderCollapse: 'collapse'}}>
                    <thead>
                        <tr style={{color: '#888', textAlign: 'right'}}>
                            <th style={{...cell, textAlign: 'left'}}>From</th><th style={cell}>To</th><th style={cell}>Fee</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bands.map((b) => (
                            <tr key={b.min} style={{textAlign: 'right'}}>
                                <td style={{...cell, textAlign: 'left'}}>{fmt(b.min)}</td>
                                <td style={cell}>{fmt(b.max)}</td>
                                <td style={{...cell, fontWeight: 600}}>{fmt(b.fee)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {editing && (
                <div>
                    <div style={{fontSize: 11, color: '#888', marginBottom: 6}}>
                        Paste the operator's whole withdraw-fee table (copy it from their website). One band per line:
                        from, to, fee — commas and header rows are fine.
                    </div>
                    <textarea
                        value={text}
                        onChange={(e) => { setText(e.target.value); setPreview(null); }}
                        rows={12}
                        style={{width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 8, borderRadius: 8, border: '1px solid #eee'}}
                        placeholder={'100\t999\t50\n1,000\t2,499\t100\n...'}
                    />
                    {preview && (
                        <div style={{margin: '8px 0', fontSize: 12}}>
                            <b>{preview.length} bands read.</b>{' '}
                            {issues.length === 0
                                ? <span style={{color: '#15803d'}}>No gaps or overlaps.</span>
                                : <ul style={{color: '#b91c1c', margin: '4px 0 0 16px'}}>{issues.map((i) => <li key={i}>{i}</li>)}</ul>}
                        </div>
                    )}
                    <div style={{display: 'flex', gap: 8, marginTop: 8}}>
                        {!preview ? (
                            <button style={btn} onClick={() => setPreview(parseFeeTable(text))}>Check</button>
                        ) : (
                            <button
                                style={{...btn, background: '#29327b', color: '#fff'}}
                                disabled={preview.length === 0}
                                onClick={async () => {
                                    await onSave({
                                        bands: preview,
                                        updatedAt: new Date().toISOString(),
                                        sourceUrl: value?.sourceUrl || op.source,
                                    });
                                    setEditing(false);
                                    setPreview(null);
                                }}
                            >
                                Save {preview.length} bands{issues.length ? ' anyway' : ''}
                            </button>
                        )}
                        <button style={{...btn, background: '#fff', color: '#888', borderColor: '#eee'}}
                                onClick={() => { setEditing(false); setPreview(null); }}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const btn: React.CSSProperties = {
    fontSize: 11, color: '#29327b', border: '1px solid #c7d2fe', borderRadius: 6,
    padding: '4px 12px', cursor: 'pointer', background: '#eef2ff',
};
const cell: React.CSSProperties = {padding: '3px 6px', borderBottom: '1px solid #f7f7f7'};

/** The cash-out levy (both networks) — e.g. 0.05% on amounts above K100,000. */
const LevyRow = ({value, onSave}: {value?: {rate: number; above: number}; onSave: (v: {rate: number; above: number}) => Promise<void>}) => {
    const [editing, setEditing] = useState(false);
    const [pct, setPct] = useState('');
    const [above, setAbove] = useState('');
    useEffect(() => {
        setPct(value ? String(+(value.rate * 100).toFixed(4)) : '');
        setAbove(value ? String(value.above) : '');
    }, [value]);
    const input: React.CSSProperties = {width: 90, fontSize: 12, padding: '3px 6px', border: '1px solid #ddd', borderRadius: 6};
    return (
        <div style={{borderTop: '1px solid #f5f5f5', padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
            <div>
                <div style={{fontSize: 13, fontWeight: 600, color: '#333'}}>Cash-out levy (both networks)</div>
                <div style={{fontSize: 11, color: '#aaa'}}>
                    {value?.rate ? `${+(value.rate * 100).toFixed(4)}% on cash-outs above MWK ${fmt(value.above)} — added to the band fee` : 'Not set'}
                </div>
            </div>
            {editing ? (
                <div style={{display: 'flex', gap: 6, alignItems: 'center', fontSize: 12}}>
                    <input style={input} value={pct} onChange={(e) => setPct(e.target.value)} placeholder="0.05"/> % above MWK
                    <input style={input} value={above} onChange={(e) => setAbove(e.target.value)} placeholder="100000"/>
                    <button style={{...btn, background: '#29327b', color: '#fff'}} onClick={async () => {
                        const rate = Number(pct) / 100;
                        const min = Number(above.replace(/,/g, ''));
                        if (!(rate >= 0 && rate < 0.1) || !(min >= 0)) return;
                        await onSave({rate, above: min});
                        setEditing(false);
                    }}>Save</button>
                    <button style={{...btn, background: '#fff', color: '#888', borderColor: '#eee'}} onClick={() => setEditing(false)}>Cancel</button>
                </div>
            ) : (
                <button style={btn} onClick={() => setEditing(true)}>{value?.rate ? 'Edit' : 'Add'}</button>
            )}
        </div>
    );
};

/** Admin editor for the operators' cash-out fee tables (batch paste + check). */
export const WithdrawalFeesEditor = ({value, onSave}: {
    value?: WithdrawalFees;
    onSave: (fees: WithdrawalFees) => Promise<void>;
}) => (
    <div>
        {OPERATORS.map((op) => (
            <OperatorTable
                key={op.key}
                op={op}
                value={value?.[op.key]}
                onSave={(fees) => onSave({...(value ?? {}), [op.key]: fees})}
            />
        ))}
        <LevyRow value={value?.levy} onSave={(levy) => onSave({...(value ?? {}), levy})}/>
    </div>
);
